import { inject, Injectable, PLATFORM_ID, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  refreshExpiresAt: number;
}

const STORAGE_KEY = 'accounting_auth_session';
const TOKEN_URL = `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/token`;
const LOGOUT_URL = `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/logout`;
const FORM_HEADERS = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _tokens = signal<AuthTokens | null>(this.loadFromStorage());

  readonly isAuthenticated = computed(() => {
    const tokens = this._tokens();
    return !!tokens && tokens.expiresAt > Date.now();
  });

  readonly accessToken = computed(() => this._tokens()?.accessToken ?? null);

  readonly sessionExpiresAt = computed(() => this._tokens()?.refreshExpiresAt ?? null);

  readonly accessTokenExpiresAt = computed(() => this._tokens()?.expiresAt ?? null);

  login(username: string, password: string): Observable<void> {
    const body = new URLSearchParams({
      grant_type: 'password',
      client_id: environment.keycloak.clientId,
      username,
      password,
      scope: 'openid',
    });

    return this.http
      .post<KeycloakTokenResponse>(TOKEN_URL, body.toString(), { headers: FORM_HEADERS })
      .pipe(
        tap((res) => this.storeTokens(res)),
        map(() => void 0),
      );
  }

  refreshAccessToken(): Observable<void> {
    const tokens = this._tokens();
    if (!tokens) return throwError(() => new Error('No session'));

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: environment.keycloak.clientId,
      refresh_token: tokens.refreshToken,
    });

    return this.http
      .post<KeycloakTokenResponse>(TOKEN_URL, body.toString(), { headers: FORM_HEADERS })
      .pipe(
        tap((res) => this.storeTokens(res)),
        map(() => void 0),
      );
  }

  logout(): void {
    const tokens = this._tokens();
    if (tokens) {
      const body = new URLSearchParams({
        client_id: environment.keycloak.clientId,
        refresh_token: tokens.refreshToken,
      });
      this.http
        .post(LOGOUT_URL, body.toString(), { headers: FORM_HEADERS })
        .subscribe({ error: () => {} });
    }

    this._tokens.set(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(STORAGE_KEY);
    }
    this.router.navigate(['/auth/login']);
  }

  forgotPasswordUrl(redirectUri: string): string {
    const params = new URLSearchParams({
      client_id: environment.keycloak.clientId,
      redirect_uri: redirectUri,
    });
    return `${environment.keycloak.url}/realms/${environment.keycloak.realm}/login-actions/reset-credentials?${params}`;
  }

  private storeTokens(res: KeycloakTokenResponse): void {
    const tokens: AuthTokens = {
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
      expiresAt: Date.now() + res.expires_in * 1000,
      refreshExpiresAt: Date.now() + res.refresh_expires_in * 1000,
    };
    this._tokens.set(tokens);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    }
  }

  private loadFromStorage(): AuthTokens | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const tokens = JSON.parse(raw) as AuthTokens;
      if (tokens.refreshExpiresAt <= Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return tokens;
    } catch {
      return null;
    }
  }
}

interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}
